import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { firestore } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  localeDir,
  normalizeLocale,
  type AppLocale,
} from '@/i18n/locales';

type LocaleContextValue = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  dir: 'ltr' | 'rtl';
  labels: typeof LOCALE_LABELS;
  supportedLocales: typeof SUPPORTED_LOCALES;
};

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function initialLocale(): AppLocale {
  if (typeof window === 'undefined') return 'en';
  return normalizeLocale(
    window.localStorage.getItem('agentcad.locale') ?? navigator.language,
  );
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<AppLocale>(initialLocale);
  const [loadedSettingsFor, setLoadedSettingsFor] = useState<string | null>(null);

  const setLocale = (next: AppLocale) => {
    setLocaleState(next);
  };

  useEffect(() => {
    if (!user?.id) {
      setLoadedSettingsFor(null);
      return;
    }

    const userId = user.id;
    let cancelled = false;
    setLoadedSettingsFor(null);

    async function loadUserLocale() {
      const ref = doc(firestore, 'userSettings', userId);
      const snap = await getDoc(ref);
      if (cancelled) return;

      const remoteLocale = normalizeLocale(snap.data()?.locale);
      if (snap.exists() && snap.data()?.locale) {
        setLocaleState(remoteLocale);
      }
      setLoadedSettingsFor(userId);
    }

    loadUserLocale().catch((err) => {
      console.warn('[LocaleProvider] failed to load locale setting', err);
      if (!cancelled) setLoadedSettingsFor(userId);
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDir(locale);
    window.localStorage.setItem('agentcad.locale', locale);
    void i18n.changeLanguage(locale);
  }, [i18n, locale]);

  useEffect(() => {
    if (!user?.id || loadedSettingsFor !== user.id) return;

    const ref = doc(firestore, 'userSettings', user.id);
    setDoc(ref, { locale }, { merge: true }).catch((err) => {
      console.warn('[LocaleProvider] failed to persist locale setting', err);
    });
  }, [loadedSettingsFor, locale, user?.id]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      dir: localeDir(locale),
      labels: LOCALE_LABELS,
      supportedLocales: SUPPORTED_LOCALES,
    }),
    [locale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}

